import { Injectable } from '@angular/core';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { TranslationService, SupportedLanguage } from './translation.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageNavigationService {
  constructor(
    private router: Router,
    private translationService: TranslationService
  ) {}

  navigate(
    path: string | string[],
    extras?: NavigationExtras
  ): Promise<boolean> {
    const lang =
      this.getCurrentLanguageFromUrl() ||
      this.translationService.getCurrentLanguage();

    const pathArray = Array.isArray(path) ? path : [path];
    const cleanedPath = this.removeLanguagePrefixFromPath(pathArray);
    const fullPath = ['/', lang, ...cleanedPath];

    return this.router.navigate(fullPath, extras);
  }

  navigateByUrl(url: string): Promise<boolean> {
    const lang =
      this.getCurrentLanguageFromUrl() ||
      this.translationService.getCurrentLanguage();

    const cleanUrl = this.removeLanguagePrefixFromUrl(url);
    const urlWithLang = `/${lang}/${cleanUrl}`;
    return this.router.navigateByUrl(urlWithLang);
  }

  getCurrentLanguageFromUrl(): SupportedLanguage | null {
    const urlSegments = this.router.url.split('/').filter((s) => s);
    const firstSegment = urlSegments[0];

    if (firstSegment === 'en' || firstSegment === 'he') {
      return firstSegment as SupportedLanguage;
    }

    return null;
  }

  getLanguagePrefixedUrl(url: string, lang?: SupportedLanguage): string {
    const language =
      lang ||
      this.getCurrentLanguageFromUrl() ||
      this.translationService.getCurrentLanguage();
    const cleanUrl = this.removeLanguagePrefixFromUrl(url);
    const prefixedUrl = `/${language}/${cleanUrl}`;
    return prefixedUrl;
  }

  private removeLanguagePrefixFromPath(pathArray: string[]): string[] {
    return pathArray
      .map((segment) =>
        segment.startsWith('/') ? segment.substring(1) : segment
      )
      .filter((segment) => segment && segment !== 'en' && segment !== 'he');
  }

  private removeLanguagePrefixFromUrl(url: string): string {
    const cleanUrl = url.startsWith('/') ? url.substring(1) : url;
    const segments = cleanUrl.split('/');

    if (segments[0] === 'en' || segments[0] === 'he') {
      return segments.slice(1).join('/');
    }

    return cleanUrl;
  }

  patchRouterForLanguageSupport() {
    const originalNavigate = this.router.navigate.bind(this.router);
    const originalNavigateByUrl = this.router.navigateByUrl.bind(this.router);

    (this.router as any).navigate = (
      commands: any[],
      extras?: NavigationExtras
    ): Promise<boolean> => {
      const lang =
        this.getCurrentLanguageFromUrl() ||
        this.translationService.getCurrentLanguage();

      if (Array.isArray(commands) && commands.length > 0) {
        const firstCommand = commands[0];

        if (firstCommand === '/' && commands.length > 1) {
          const secondCommand = commands[1];
          if (secondCommand !== 'en' && secondCommand !== 'he') {
            const restCommands = commands.slice(1);
            const newCommands = ['/', lang, ...restCommands];

            return originalNavigate(newCommands, extras);
          }

          return originalNavigate(commands, extras);
        }

        if (typeof firstCommand === 'string' && firstCommand.startsWith('/')) {
          const pathSegments = firstCommand.split('/').filter((s) => s);

          if (
            pathSegments.length > 0 &&
            pathSegments[0] !== 'en' &&
            pathSegments[0] !== 'he'
          ) {
            const restCommands = commands.slice(1);
            const newCommands = ['/', lang, ...pathSegments, ...restCommands];

            return originalNavigate(newCommands, extras);
          }
        }
      }

      return originalNavigate(commands, extras);
    };

    (this.router as any).navigateByUrl = (
      url: string | any
    ): Promise<boolean> => {
      if (typeof url === 'string') {
        const urlSegments = url
          .split('/')
          .filter((s) => s && !s.startsWith('?'));
        if (
          urlSegments.length > 0 &&
          urlSegments[0] !== 'en' &&
          urlSegments[0] !== 'he'
        ) {
          const lang =
            this.getCurrentLanguageFromUrl() ||
            this.translationService.getCurrentLanguage();
          const cleanUrl = this.removeLanguagePrefixFromUrl(url);
          const newUrl = `/${lang}/${cleanUrl}`;
          return originalNavigateByUrl(newUrl);
        }
      }

      return originalNavigateByUrl(url);
    };
  }
}
